import { io, Socket } from 'socket.io-client';

import { PrintEvents } from 'root/tracking.types';

import Logger from '../utils/logger';

import { HUB_MESSAGES } from './types';
import { resolveOnTimeout } from './utils';

export const HUB_SERVER_PORT = 3012;

type SocketResponse = { status: 'ok' } | undefined;

export class HubLocalSocketClient {
  private socket: Socket | undefined;
  private channelNameSent: string | undefined;

  private timeout = 1000; // If the hub server is running, it should connect very quickly

  private connecting = false;

  public async connect(channelName: string) {
    if (this.socket?.connected || this.connecting) {
      return;
    }
    Logger.debug('Hub local server connecting');

    this.socket = io(`ws://localhost:${HUB_SERVER_PORT}`, {
      port: HUB_SERVER_PORT.toString(),
      autoConnect: false,
      reconnectionDelay: 30_000,
      reconnectionDelayMax: 30_000,
    });

    this.socket.on('connect_error', err => {
      Logger.debug('Hub local server connect_error', err);
    });

    this.connecting = true;

    const connectPromise = new Promise<void>(resolve => {
      this.socket?.once('connect', resolve);
    });

    this.socket.once('connect', this.handleConnection);

    this.socket.on(HUB_MESSAGES.GET_CHANNEL_NAME, async () => {
      await this.sendChannelName(channelName);
      await this.disconnect();
    });

    this.socket.connect();

    await resolveOnTimeout(connectPromise, this.timeout);
  }

  public async sendChannelName(channelName: string) {
    if (this.channelNameSent === channelName || !this.socket?.connected) {
      return;
    }
    Logger.debug('Send channel name to hub');

    const channelSent = this.sendEvent(HUB_MESSAGES.UPDATE_CHANNEL_NAME, channelName);

    await resolveOnTimeout(channelSent, this.timeout);

    this.channelNameSent = channelName;
  }

  private sendEvent(eventName: HUB_MESSAGES, channelName: string) {
    return new Promise<void>(resolve =>
      this.socket?.emit(eventName, channelName, (response: SocketResponse) => {
        if (response?.status === 'ok') {
          this.logEvent();
        }
        return resolve();
      })
    );
  }

  private logEvent() {
    window.dataLayer.push({
      event: PrintEvents.HubLocalServer,
    });
  }

  public async disconnect() {
    if (!this.socket?.connected) {
      return;
    }
    Logger.debug('Disconnect hub local server');

    const disconnect = new Promise(resolve => this.socket?.on('disconnect', resolve));
    this.socket?.close();
    await resolveOnTimeout(disconnect, this.timeout);
  }

  private handleConnection = async () => {
    Logger.debug('Hub local server connected');
    this.connecting = false;
  };
}
